<template>
  <v-card style="overflow: hidden;">
    <v-toolbar flat dense color="secondary" dark>
      <v-toolbar-title class="body-2">{{ title }}</v-toolbar-title>
      <v-spacer />
      <v-btn-toggle
        v-if="dateRangeEditable"
        v-model="selectedDateRange"
        mandatory
        class="date-range-toggle"
        @change="onDateRangeChange"
      >
        <v-btn
          v-for="(item, idx) in dateRangeShortcuts"
          :key="idx"
          flat
          small
          :value="item.value"
          :title="item.label"
          class="text-capitalize"
        >
          {{ item.shortLabel }}
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>

    <v-progress-linear indeterminate color="blue" class="my-0" v-if="loading" />

    <v-alert
      outline
      class="py-1 ma-0"
      color="warning"
      icon="assessment"
      :value="true"
      v-if="error"
    >
      <div class="primary--text">{{ errorMessage }}</div>
    </v-alert>

    <div v-else-if="dataForGraph.length > 0">
      <v-layout v-if="dataForGraph[0].length > 0">
        <v-flex xs12 lg12>
          <h3 class="pa-2">Exceedance Data</h3>
        </v-flex>
      </v-layout>
      <v-layout v-for="analyteData in dataForGraph[0]" :key="loadId + '-ex-' + analyteData.Analyte">
        <v-flex xs6 lg6>
          <FortisDashboardGraph
            :analyte="analyteData.Analyte"
            :location-type="'BC Rail Site'"
            :datasets="datasetsFor(analyteData, 'BC Rail Site')"
            :query-date-range="dateRange"
          ></FortisDashboardGraph>
        </v-flex>
        <v-flex xs6 lg6>
          <FortisDashboardGraph
            :analyte="analyteData.Analyte"
            :location-type="'Woodfibre LNG Site'"
            :datasets="datasetsFor(analyteData, 'Woodfibre LNG Site')"
            :query-date-range="dateRange"
          ></FortisDashboardGraph>
        </v-flex>
      </v-layout>

      <v-layout v-if="dataForGraph[1].length > 0">
        <v-flex xs12 lg12>
          <h3 class="pa-2">Analyte Data</h3>
        </v-flex>
      </v-layout>
      <v-layout v-for="analyteData in dataForGraph[1]" :key="loadId + '-an-' + analyteData.Analyte">
        <v-flex xs6 lg6>
          <FortisDashboardGraph
            :analyte="analyteData.Analyte"
            :location-type="'BC Rail Site'"
            :datasets="datasetsFor(analyteData, 'BC Rail Site')"
            :query-date-range="dateRange"
          ></FortisDashboardGraph>
        </v-flex>
        <v-flex xs6 lg6>
          <FortisDashboardGraph
            :analyte="analyteData.Analyte"
            :location-type="'Woodfibre LNG Site'"
            :datasets="datasetsFor(analyteData, 'Woodfibre LNG Site')"
            :query-date-range="dateRange"
          ></FortisDashboardGraph>
        </v-flex>
      </v-layout>
    </div>
  </v-card>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import FortisDashboardGraph from '../FortisDashboardGraph'
import { dateRangePresets, dateRangeShortcuts as dateRangeShortcutOptions } from './dateRange'

export default {
  name: 'FortisTenantDashboardWidget',
  components: {
    FortisDashboardGraph,
  },
  props: {
    widgetId: { type: String, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: null,
      selectedDateRange: this.config.dateRange || 'pastTwoWeeks',
      dataForGraph: [],
      loadId: 0,
    }
  },
  computed: {
    title() {
      return this.config.title || 'Tenant Dashboard'
    },
    dateRangeEditable() {
      return this.config.dateRangeEditable !== false
    },
    dateRangeShortcuts() {
      return dateRangeShortcutOptions
    },
    dateRange() {
      const preset = this.selectedDateRange || 'pastTwoWeeks'
      const days = dateRangePresets[preset] || 14
      return {
        preset,
        start: moment().subtract(days, 'days'),
        end: moment(),
      }
    },
  },
  methods: {
    async initialize() {
      await this.loadData()
    },
    async refresh() {
      await this.loadData()
    },
    onDateRangeChange() {
      this.refresh()
    },
    async loadData() {
      this.loading = true
      this.error = false
      this.errorMessage = null
      try {
        const responseData = await this.fetchData()
        if (responseData == null || responseData.length === 0) {
          this.error = true
          this.errorMessage = 'No data available for the selected time range.'
          this.dataForGraph = []
          this.loading = false
          return
        }
        this.dataForGraph = this.filterDataForGraph(responseData)
        this.loadId += 1
        this.loading = false
      } catch (err) {
        this.error = true
        this.errorMessage = (err && err.message) || 'Failed to load data.'
        this.loading = false
      }
    },
    async fetchData() {
      const start = this.dateRange.start
      const end = this.dateRange.end
      const url =
        'api/TenantDashboard/Graph/' +
        start.format('YYYY-MM-DD') +
        '/' +
        end.format('YYYY-MM-DD')
      const res = await axios.get(url)
      return res.data
    },
    datasetsFor(analyteData, locationType) {
      const match = (analyteData.Data || []).find(x => x.LocationType === locationType)
      return match ? match.Data : []
    },
    filterDataForGraph(analytesData) {
      var analytesWithExccedance = [...new Set(analytesData.filter(x => x.HasExceedance).map(x => x.Analyte))]
      var analytesWithoutExccedance = [...new Set(analytesData.filter(x => !x.HasExceedance).map(x => x.Analyte))]
      var locationTypes = [...new Set(analytesData.map(x => x.LocationType))]

      var exccedanceData = []
      analytesWithExccedance.forEach((a) => {
        exccedanceData.push({ Analyte: a, Data: this.filterDataOfAnalyteForGraph(locationTypes, a, analytesData) })
      })

      var noExccedanceData = []
      analytesWithoutExccedance.forEach((a) => {
        noExccedanceData.push({ Analyte: a, Data: this.filterDataOfAnalyteForGraph(locationTypes, a, analytesData) })
      })

      return [exccedanceData, noExccedanceData]
    },
    filterDataOfAnalyteForGraph(locationTypes, analyte, data) {
      var dataWithAnalyte = data.filter(x => x.Analyte == analyte)
      return locationTypes.map(lt => {
        var dataOfType = dataWithAnalyte.filter(x => x.LocationType == lt).map((x) => {
          return { Location: x.Location, Points: x.Points, Guideline: x.GuidelineValues }
        })
        return { LocationType: lt, Data: dataOfType }
      })
    },
  },
}
</script>

<style scoped>
.date-range-toggle {
  margin-right: 8px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 4px;
}
</style>
